// 🌓 OWLY CRM - Theme Context for Dark/Light Mode
import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark'; // El tema real aplicado (resuelto desde system)
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'owly-crm-theme',
}: ThemeProviderProps) {
  // Leer tema guardado o usar default
  const getInitialTheme = (): Theme => {
    const saved = localStorage.getItem(storageKey) as Theme | null;
    if (saved && ['light', 'dark', 'system'].includes(saved)) return saved;
    return defaultTheme;
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light');

  // Aplicar tema guardado al DOM inmediatamente
  React.useLayoutEffect(() => {
    const root = window.document.documentElement;
    const saved = localStorage.getItem(storageKey) as Theme | null;
    const initial = saved && ['light', 'dark', 'system'].includes(saved) ? saved : defaultTheme;

    const resolved = initial === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : initial as 'light' | 'dark';

    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    setActualTheme(resolved);

    if (!saved) localStorage.setItem(storageKey, defaultTheme);
  }, [storageKey, defaultTheme]);

  // Función para obtener el tema del sistema
  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Función para aplicar el tema al DOM
  const applyTheme = React.useCallback((newTheme: 'light' | 'dark') => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);
    setActualTheme(newTheme);
  }, []);

  // Función para resolver el tema actual
  const resolveTheme = React.useCallback((selectedTheme: Theme): 'light' | 'dark' => {
    if (selectedTheme === 'system') {
      return getSystemTheme();
    }
    return selectedTheme;
  }, []);

  // Aplicar tema cuando cambia
  useEffect(() => {
    const resolvedTheme = resolveTheme(theme);
    applyTheme(resolvedTheme);
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey, applyTheme, resolveTheme]);

  // Escuchar cambios en las preferencias del sistema
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const resolvedTheme = resolveTheme('system');
      applyTheme(resolvedTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Función para cambiar tema
  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  // Función para alternar entre light y dark (ignora system)
  const toggleTheme = () => {
    if (theme === 'system' || theme === 'light') {
      setTheme('dark');
    } else {
      setTheme('light');
    }
  };

  const value: ThemeContextType = {
    theme,
    actualTheme,
    setTheme: handleSetTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook personalizado para usar el tema
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Componente ThemeToggle reutilizable
export function ThemeToggle({
  className = "",
  showLabel = true
}: {
  className?: string;
  showLabel?: boolean;
}) {
  const { theme, actualTheme, setTheme, toggleTheme } = useTheme();

  const icons = {
    light: '☀️',
    dark: '🌙',
    system: '💻'
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Tema:
        </span>
      )}

      {/* Toggle simple light/dark */}
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        title={`Cambiar a ${actualTheme === 'light' ? 'modo oscuro' : 'modo claro'}`}
      >
        <span className="text-lg">
          {actualTheme === 'light' ? '🌙' : '☀️'}
        </span>
      </button>

      {/* Selector completo (opcional) */}
      <select
        value={theme}
        onChange={(e) => setTheme(e.target.value as Theme)}
        className="text-sm border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        title="Seleccionar tema"
      >
        <option value="light">☀️ Claro</option>
        <option value="dark">🌙 Oscuro</option>
        <option value="system">💻 Sistema</option>
      </select>
    </div>
  );
}

// Función utilitaria para obtener clases CSS según el tema
export function getThemeClasses(lightClasses: string, darkClasses: string): string {
  return `${lightClasses} dark:${darkClasses}`;
}

// Hook para detectar si está en dark mode
export function useDarkMode(): boolean {
  const { actualTheme } = useTheme();
  return actualTheme === 'dark';
}